import {
  AssessmentQuestionType,
  AssessmentSignatureUserInput,
  ContactType,
  CustomAssessmentQuery,
  CustomAssessmentQuestionInput,
  SignatureUser,
} from '../../../generated/graphql';

import {
  isFreeTextAssessmentQuestion,
  isInitialsAssessmentQuestion,
  isMultiSelectAssessmentQuestion,
  isMultiSignatureAssessmentQuestion,
  isMultipleChoiceAssessmentQuestion,
  isRichTextAssessmentQuestion,
  isSignatureAssessmentQuestion,
  isStatementAssessmentQuestion,
  isFileUploadAssessmentQuestion,
  isAutofillAssessmentQuestion,
  isDatePickerAssessmentQuestion,
  isProviderSignatureAssessmentQuestion,
  isPatientContactAssessmentQuestion,
} from '../../lib/assessments';

import { Question, QuestionForm } from './QuestionContainer/helpers';
import { UseFormSetError } from 'react-hook-form';
import { customToast } from '../../components/ToastAlert/customToast';
import { ALL_FILE_UPLOAD_EXTENSIONS } from '../../lib/fileUtils';

export const defaultQuestionSchema: Question = {
  questionType: AssessmentQuestionType.MultipleChoice,
  isRequired: true,
  question: '',
  answerOptions: ['', ''],
  assessmentUserOrderIndices: [],
  allowedExtensions: Array.from(ALL_FILE_UPLOAD_EXTENSIONS),
  autofillSource: undefined,
  defaultToToday: false,
  contactType: ContactType.EmergencyContact,
};

export const defaultSignatureUserSchemaTeam: AssessmentSignatureUserInput[] = [
  {
    signatureUserType: SignatureUser.PatientUser,
    orderIndex: 0,
  },
];

export const defaultSignatureUserSchemaIndividual: AssessmentSignatureUserInput[] =
  [
    ...defaultSignatureUserSchemaTeam,
    {
      signatureUserType: SignatureUser.SendingProviderUser,
      orderIndex: 1,
    },
  ];

type CustomAssessmentQuestions =
  CustomAssessmentQuery['assessment']['questions'];

export const formatQuestionFormData = (
  questions: Question[],
): CustomAssessmentQuestionInput[] => {
  return questions?.map((question) => ({
    questionType: question.questionType,
    isRequired: question.isRequired,
    ...(isMultipleChoiceAssessmentQuestion(question) && {
      multipleChoiceQuestion: {
        question: question.question,
        answerOptions: question.answerOptions,
      },
    }),
    ...(isMultiSelectAssessmentQuestion(question) && {
      multiSelectQuestion: {
        question: question.question,
        answerOptions: question.answerOptions,
      },
    }),
    ...(isFreeTextAssessmentQuestion(question) && {
      freeTextQuestion: {
        question: question.question,
      },
    }),
    ...(isRichTextAssessmentQuestion(question) && {
      richTextQuestion: {
        question: question.question,
      },
    }),
    ...(isInitialsAssessmentQuestion(question) && {
      initialsQuestion: {
        question: question.question,
      },
    }),
    ...(isSignatureAssessmentQuestion(question) && {
      signatureQuestion: {
        question: question.question,
      },
    }),
    ...(isMultiSignatureAssessmentQuestion(question) && {
      multiSignatureQuestion: {
        question: question.question,
        assessmentUserOrderIndices: question.assessmentUserOrderIndices,
      },
    }),
    ...(question.questionType === AssessmentQuestionType.ProviderSignature && {
      providerSignatureQuestion: {
        question: question.question,
      },
    }),
    ...(isStatementAssessmentQuestion(question) && {
      statement: {
        statement: question.question,
      },
    }),
    ...(isFileUploadAssessmentQuestion(question) && {
      fileUploadQuestion: {
        question: question.question,
        allowedExtensions: question.allowedExtensions || [],
      },
    }),
    ...(isAutofillAssessmentQuestion(question) && {
      autofillQuestion: {
        question: question.question,
        autofillSource: question.autofillSource,
      },
    }),
    ...(isDatePickerAssessmentQuestion(question) && {
      datePickerQuestion: {
        question: question.question,
        defaultToToday: question.defaultToToday,
      },
    }),
    ...(isPatientContactAssessmentQuestion(question) && {
      patientContactQuestion: {
        question: question.question,
        contactType: question.contactType,
      },
    }),
  }));
};

export const formatQuestionDataForForm = (
  questions: CustomAssessmentQuestions,
): Question[] => {
  return questions?.map((question) => {
    // Base schema - remove answerOptions for non-multiple-choice questions
    const baseSchema = { ...defaultQuestionSchema };
    // Only keep answerOptions for multiple choice/multi-select questions
    if (
      question.questionType !== AssessmentQuestionType.MultipleChoice &&
      question.questionType !== AssessmentQuestionType.MultiSelect &&
      question.questionType !== AssessmentQuestionType.MultipleChoiceScored
    ) {
      delete baseSchema.answerOptions;
    }

    // Ensure question and isRequired are set for all question types as fallback
    const fallbackData: Partial<Question> = {};
    if ('question' in question && question.question !== undefined) {
      fallbackData.question = question.question;
    }
    if ('isRequired' in question && question.isRequired !== undefined) {
      fallbackData.isRequired = question.isRequired;
    }

    return {
      ...baseSchema,
      questionType: question.questionType,
      ...fallbackData,
      ...(isMultipleChoiceAssessmentQuestion(question) && {
        isRequired: question.isRequired,
        question: question?.question,
        answerOptions: question?.answerOptions?.map(
          (answerOption) => answerOption?.answerOption,
        ),
      }),
      ...(isMultiSelectAssessmentQuestion(question) && {
        isRequired: question.isRequired,
        question: question?.question,
        answerOptions: question?.answerOptions?.map(
          (answerOption) => answerOption?.answerOption,
        ),
      }),
      ...(isFreeTextAssessmentQuestion(question) && {
        isRequired: question.isRequired,
        question: question?.question,
      }),
      ...(isRichTextAssessmentQuestion(question) && {
        isRequired: question.isRequired,
        question: question?.question,
      }),
      ...(isInitialsAssessmentQuestion(question) && {
        isRequired: question.isRequired,
        question: question?.question,
      }),
      ...(isSignatureAssessmentQuestion(question) && {
        isRequired: question.isRequired,
        question: question?.question,
      }),
      ...(isMultiSignatureAssessmentQuestion(question) && {
        isRequired: question.isRequired,
        question: question?.question,
        assessmentUserOrderIndices: question?.assessmentSignatureOrderIndices,
      }),
      ...(isProviderSignatureAssessmentQuestion(question) && {
        isRequired: question.isRequired ?? true,
        question: question.question || '',
      }),
      ...(isStatementAssessmentQuestion(question) && {
        isRequired: question.isRequired,
        question: question?.question,
      }),
      ...(isFileUploadAssessmentQuestion(question) && {
        isRequired: question.isRequired,
        question: question?.question,
        allowedExtensions: question.allowedExtensions || [],
      }),
      ...(isAutofillAssessmentQuestion(question) && {
        isRequired: question.isRequired,
        question: question?.question,
        autofillSource: question?.autofillSource,
      }),
      ...(isDatePickerAssessmentQuestion(question) && {
        isRequired: question.isRequired,
        question: question?.question,
        defaultToToday: question?.defaultToToday,
      }),
      ...(isPatientContactAssessmentQuestion(question) && {
        isRequired: question.isRequired,
        question: question?.question,
        contactType: question.contactType,
      }),
    };
  });
};

export const isMultiSignatureValid = (
  questions: CustomAssessmentQuestionInput[],
  assessmentSignatureUsers: AssessmentSignatureUserInput[],
  setError: UseFormSetError<QuestionForm>,
) => {
  if (assessmentSignatureUsers.length < 2) {
    customToast.warning(
      `Please add at least 2 signatures for multi-signature questions`,
    );
    return false;
  }

  const validationErrors = [];
  const usedSignatureUsers = new Set<number>();

  questions.forEach((question, index) => {
    if (question.questionType === AssessmentQuestionType.MultiSignature) {
      const questionSignatures =
        question.multiSignatureQuestion?.assessmentUserOrderIndices || [];

      if (questionSignatures.length < 1) {
        validationErrors.push({
          field: `questions.${index}.question`,
          message: `Each multi-signature question must have at least one signer.`,
        });
      }

      questionSignatures.forEach((orderIndex) =>
        usedSignatureUsers.add(orderIndex),
      );
    }
  });

  const inactiveUsers = assessmentSignatureUsers.filter(
    (user) => !usedSignatureUsers.has(user.orderIndex),
  );

  if (inactiveUsers.length > 0) {
    customToast.warning(
      `Each signer must be active in at least one multi-signature question.`,
    );
    return false;
  }

  if (validationErrors.length > 0) {
    validationErrors.forEach(({ field, message }) =>
      setError(field, { message }),
    );
    return false;
  }

  return true;
};

export const isNotStatementOnlyForm = (
  questions: CustomAssessmentQuestionInput[],
): boolean => {
  const allStatementsCount = questions.filter(
    (question) => question.questionType === AssessmentQuestionType.Statement,
  ).length;

  if (allStatementsCount === questions.length && questions.length > 0) {
    customToast.warning(
      'Forms with only statements are not supported. Instead, create a Text Document from the Library.',
    );
    return false;
  }

  return true;
};

export const isFormQuestionsValid = (
  questions: CustomAssessmentQuestionInput[],
  assessmentSignatureUsers: AssessmentSignatureUserInput[],
  setError: UseFormSetError<QuestionForm>,
  hasMultiSignature: boolean,
  completableByProviderOnly?: boolean,
): boolean => {
  // Validate that provider-only forms don't have client signature questions
  if (completableByProviderOnly) {
    const clientSignatureQuestionTypes = [
      AssessmentQuestionType.Initials,
      AssessmentQuestionType.Signature,
      AssessmentQuestionType.MultiSignature,
    ];

    const hasClientSignatureQuestions = questions.some((question) =>
      clientSignatureQuestionTypes.includes(question.questionType),
    );

    if (hasClientSignatureQuestions) {
      customToast.error(
        'Provider-only forms cannot have client signature, client initials, or collaborative signature questions. Use provider signature questions instead.',
      );
      return false;
    }

    const hasPatientContactQuestions = questions.some(
      (question) =>
        question.questionType === AssessmentQuestionType.PatientContact,
    );
    if (hasPatientContactQuestions) {
      customToast.error(
        'Provider-only forms cannot include Client Contact questions. Use a client form instead.',
      );
      return false;
    }
  }

  return (
    isNotStatementOnlyForm(questions) &&
    (!hasMultiSignature ||
      isMultiSignatureValid(questions, assessmentSignatureUsers, setError))
  );
};
